import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuditChangeEntry, AuditLoginEntry, AuditViewEntry, UserSummary } from '@todo/shared';
import { auditApi } from '../api/audit.api';
import { userApi } from '../api/user.api';
import { departmentApi } from '../api/department.api';
import { projectApi } from '../api/project.api';
import { roleApi } from '../api/role.api';
import { useAuth } from '../hooks/useAuth';
import { Dropdown } from '../components/ui';
import { SidePanel } from '../components/SidePanel';

/**
 * The Audit page (Admins / Super Admins).
 *
 * Two feeds behind tabs:
 *   Changes - every write, straight from change_log (retroactive: the
 *             table has recorded edits since the app's first day)
 *   Views   - who opened which task / person, from access_log
 *             (one row per viewer+entity+hour; kept 90 days)
 *
 * Hiding this page for non-admins is courtesy - the API's 403 is the
 * actual control.
 */

const PAGE_SIZE = 50;

/** change_log verbs -> words a human reads. */
const VERB_LABEL: Record<string, string> = {
  CREATED: 'Created',
  UPDATED: 'Edited',
  DELETED: 'Deleted',
  STATUS_CHANGED: 'Changed status of',
  ASSIGNEES_CHANGED: 'Changed assignees of',
  PASSWORD_RESET: 'Reset the password of',
  IMPORTANT_CHANGED: 'Starred / unstarred',
  COMMENTED: 'Commented on',
  ATTACHMENT_ADDED: 'Attached a file to',
  ATTACHMENT_REMOVED: 'Removed a file from',
  // Folder
  SHARED: 'Shared',
  UNSHARED: 'Revoked a share on',
  EXPIRED: 'Auto-deleted (expiry reached)',
  AI_CHAT: 'Ran via AI Chat:',
};

function verbLabel(verb: string): string {
  const label = VERB_LABEL[verb] ?? verb.toLowerCase().replace(/_/g, ' ');
  // First letter always capitalised, mapped or not.
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Field keys -> the words the admin reads. */
const FIELD_LABEL: Record<string, string> = {
  displayName: 'Name',
  email: 'Email',
  employeeId: 'Employee ID',
  jobTitle: 'Job title',
  departmentId: 'Department',
  roleId: 'Role',
  projectId: 'Project',
  leadId: 'Lead',
  assigneeIds: 'Assignees',
  createdById: 'Created by',
  completionPolicy: 'Completes when',
  repeat: 'Repeat',
  status: 'Status',
  priority: 'Priority',
  dueAt: 'Due date',
  remindAt: 'Reminder',
  title: 'Title',
  details: 'Details',
  username: 'Username',
  avatarBlobKey: 'Profile photo',
  blockedReason: 'Blocked reason',
  cancelReason: 'Cancel reason',
  taskKey: 'Task key',
};

function fieldLabel(key: string): string {
  return FIELD_LABEL[key] ?? key;
}

/**
 * Ids -> names. The diff stores raw ids (leadId, assigneeIds, departmentId,
 * ...); nobody should have to read a UUID. One map per kind, built from the
 * lists the page loads anyway; an id we cannot resolve (deleted row, or a
 * person beyond the first page) falls back to a shortened id.
 */
interface Lookup {
  users: Map<string, string>;
  departments: Map<string, string>;
  projects: Map<string, string>;
  roles: Map<string, string>;
}

const ID_FIELD_KIND: Record<string, keyof Lookup> = {
  leadId: 'users',
  assigneeIds: 'users',
  createdById: 'users',
  departmentId: 'departments',
  projectId: 'projects',
  roleId: 'roles',
};

/** Values an admin should not have to read raw (ids, storage paths etc.). */
function valueLabel(field: string, value: unknown, lookup: Lookup): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'avatarBlobKey') return 'updated';
  const kind = ID_FIELD_KIND[field];
  if (kind) {
    const map = lookup[kind];
    const resolve = (id: string) => map.get(id) ?? `${id.slice(0, 8)}…`;
    // assigneeIds arrives as an array or a comma-joined string; the
    // single-id fields as one plain string. Handle all three shapes.
    const ids = Array.isArray(value)
      ? value.map(String)
      : String(value).split(',').map((s) => s.trim()).filter(Boolean);
    return ids.length === 0 ? '—' : ids.map(resolve).join(', ');
  }
  const text = String(value);
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

interface DiffEntry {
  field: string;
  /** Present on before->after edits. */
  from?: string;
  to?: string;
  /** Present on plain values (creates, legacy rows). */
  value?: string;
}

/** The diff JSON -> a uniform list the two renderers below share. */
function parseDiff(diff: string | null, lookup: Lookup): DiffEntry[] {
  if (!diff) return [];
  try {
    const parsed = JSON.parse(diff) as Record<string, unknown>;
    return Object.entries(parsed).map(([key, value]) => {
      if (value && typeof value === 'object' && 'from' in (value as object)) {
        const v = value as { from?: unknown; to?: unknown };
        return {
          field: key,
          from: valueLabel(key, v.from, lookup),
          to: valueLabel(key, v.to, lookup),
        };
      }
      return { field: key, value: valueLabel(key, value, lookup) };
    });
  } catch {
    return [{ field: 'diff', value: diff.slice(0, 120) }];
  }
}

/** The one-line coloured summary in the table: old in red, new in green. */
function DiffInline({ entries }: { entries: DiffEntry[] }) {
  return (
    <span className="text-xs">
      {entries.slice(0, 4).map((entry, i) => (
        <span key={entry.field}>
          {i > 0 && <span className="text-slate-400">, </span>}
          <span className="text-slate-500">{fieldLabel(entry.field)}: </span>
          {entry.value !== undefined ? (
            <span className="text-emerald-600">{entry.value}</span>
          ) : (
            <>
              <span className="text-red-500 line-through decoration-red-300">{entry.from}</span>
              <span className="text-slate-400"> → </span>
              <span className="text-emerald-600">{entry.to}</span>
            </>
          )}
        </span>
      ))}
      {entries.length > 4 && (
        <span className="text-slate-400"> +{entries.length - 4} more</span>
      )}
    </span>
  );
}

/**
 * The Logins tab's "From": Web / Android <version> / iOS <version>. New
 * rows already store that label; rows stamped before the label existed hold
 * a raw browser user-agent, sniffed down to the platform here.
 */
function clientLabel(agent: string | null): string {
  if (!agent) return '—';
  if (/^(Web|Android|iOS)\b/.test(agent)) return agent;
  if (/okhttp|android/i.test(agent)) return 'Android';
  if (/iphone|ipad|darwin|cfnetwork/i.test(agent)) return 'iOS';
  return 'Web';
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export default function AuditPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role.systemKey === 'SUPER_ADMIN';

  const [tab, setTab] = useState<'changes' | 'folder' | 'views' | 'logins'>('changes');
  const [entityType, setEntityType] = useState('');
  const [personId, setPersonId] = useState('');
  /** yyyy-mm-dd date range, limited by the pickers to the last 90 days
   *  (the access-log retention window). Empty = no bound. */
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  /** The pickers' allowed window: today back to 90 days ago. */
  const dateBounds = useMemo(() => {
    const today = new Date();
    const min = new Date(today.getTime() - 90 * 86_400_000);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { min: iso(min), max: iso(today) };
  }, []);

  const [people, setPeople] = useState<UserSummary[]>([]);
  const [changes, setChanges] = useState<AuditChangeEntry[]>([]);
  const [views, setViews] = useState<AuditViewEntry[]>([]);
  const [logins, setLogins] = useState<AuditLoginEntry[]>([]);
  /** Row clicked open in the detail side panel. */
  const [selected, setSelected] = useState<AuditChangeEntry | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [departmentNames, setDepartmentNames] = useState<Map<string, string>>(new Map());
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [roleNames, setRoleNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    userApi
      .list({ pageSize: 100 })
      .then((r) => setPeople(r.data))
      .catch(() => undefined);
    // Name lookups for the id fields inside diffs - best effort, ids
    // shorten gracefully if a list fails to load.
    void departmentApi.list()
      .then((rows) => setDepartmentNames(new Map(rows.map((d) => [d.id, d.name]))))
      .catch(() => undefined);
    void projectApi.list()
      .then((rows) => setProjectNames(new Map(rows.map((p) => [p.id, p.name]))))
      .catch(() => undefined);
    void roleApi.list()
      .then((rows) => setRoleNames(new Map(rows.map((r) => [r.id, r.name]))))
      .catch(() => undefined);
  }, []);

  /** Ids -> names for everything a diff can reference. */
  const lookup = useMemo<Lookup>(
    () => ({
      users: new Map(people.map((p) => [p.id, p.displayName])),
      departments: departmentNames,
      projects: projectNames,
      roles: roleNames,
    }),
    [people, departmentNames, projectNames, roleNames],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const query = {
      /*
       * The Folder tab IS the changes feed with the entity pinned - upload,
       * rename, share, unshare and delete all land in change_log as
       * `vault_file`. A separate endpoint would be a second way to ask the
       * same question, and the two would drift.
       */
      ...(tab === 'folder'
        ? { entityType: 'vault_file' }
        // Entity type is meaningless for logins - people are the rows there.
        : entityType && tab !== 'logins' ? { entityType } : {}),
      ...(personId ? { userId: personId } : {}),
      // Inclusive from-midnight, exclusive next-midnight after "to".
      ...(fromDate ? { from: new Date(`${fromDate}T00:00:00`).toISOString() } : {}),
      ...(toDate
        ? { to: new Date(new Date(`${toDate}T00:00:00`).getTime() + 86_400_000).toISOString() }
        : {}),
      page,
      pageSize: PAGE_SIZE,
    };
    try {
      if (tab === 'changes' || tab === 'folder') {
        const r = await auditApi.changes(query);
        setChanges(r.data);
        setTotal(r.meta?.total ?? r.data.length);
      } else if (tab === 'views') {
        const r = await auditApi.views(query);
        setViews(r.data);
        setTotal(r.meta?.total ?? r.data.length);
      } else {
        const r = await auditApi.logins(query);
        setLogins(r.data);
        setTotal(r.meta?.total ?? r.data.length);
      }
    } catch {
      setError('Could not load the audit trail.');
    } finally {
      setLoading(false);
    }
  }, [tab, entityType, personId, fromDate, toDate, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-slate-500">
          Only the Super Admin can view the audit trail.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Audit trail</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Who changed, viewed and signed in. Changes are kept forever; views for 90 days.
        </p>
      </header>

      {/* Tabs + filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {(['changes', 'folder', 'views', 'logins'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); setPage(1); }}
              className={[
                'rounded-md px-3.5 py-1.5 text-sm font-medium capitalize transition',
                tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Pinned on the Folder tab, so the control would only confuse. */}
        {tab !== 'logins' && tab !== 'folder' && (
          <Dropdown
            className="w-40"
            /* Four fixed values that will not grow - a search box here is
               a thing to dismiss, not a help. */
            searchable={false}
            value={entityType}
            onChange={(v) => { setEntityType(v); setPage(1); }}
            options={[
              { value: '', label: 'Anything' },
              { value: 'task', label: 'Tasks' },
              { value: 'user', label: 'People' },
              { value: 'vault_file', label: 'Folder files' },
            ]}
          />
        )}

        <Dropdown
          className="w-52"
          value={personId}
          onChange={(v) => { setPersonId(v); setPage(1); }}
          options={[
            { value: '', label: 'Anyone' },
            ...people.map((p) => ({ value: p.id, label: p.displayName })),
          ]}
        />

        {/* Date range - pickers limited to the last 90 days (the views
            retention window). Blank = no bound, so older CHANGES stay
            reachable by simply not setting a range. */}
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={fromDate}
            min={dateBounds.min}
            max={toDate || dateBounds.max}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            aria-label="From date"
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            value={toDate}
            min={fromDate || dateBounds.min}
            max={dateBounds.max}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            aria-label="To date"
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
          {error}
        </p>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2.5 font-semibold">
                {tab === 'logins' ? 'Last login' : 'When'}
              </th>
              <th className="px-4 py-2.5 font-semibold">Who</th>
              <th className="px-4 py-2.5 font-semibold">
                {tab === 'changes' || tab === 'folder' ? 'Did what' : tab === 'views' ? 'Opened' : 'Role'}
              </th>
              <th className="px-4 py-2.5 font-semibold">
                {tab === 'changes' || tab === 'folder' ? 'Details' : 'From'}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && tab === 'changes' && changes.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No changes match.</td></tr>
            )}
            {!loading && tab === 'views' && views.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No views recorded yet.</td></tr>
            )}
            {!loading && tab === 'logins' && logins.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No logins match.</td></tr>
            )}

            {!loading && (tab === 'changes' || tab === 'folder') && changes.map((entry, i) => (
              <tr
                key={i}
                onClick={() => setSelected(entry)}
                className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50"
                title="Click for the full change"
              >
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                  {formatWhen(entry.createdAt)}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 font-medium text-slate-900">
                  {entry.actorName ?? 'System'}
                </td>
                <td className="px-4 py-2.5 text-slate-700">
                  {verbLabel(entry.verb)}{' '}
                  <span className="font-mono text-xs text-slate-500">
                    {entry.entityLabel ?? entry.entityId.slice(0, 8)}
                  </span>{' '}
                  <span className="text-xs text-slate-400">
                    ({entry.entityType === 'vault_file' ? 'folder file' : entry.entityType === 'chat' ? 'AI Chat' : entry.entityType})
                  </span>
                </td>
                <td className="max-w-md truncate px-4 py-2.5">
                  <DiffInline entries={parseDiff(entry.diff, lookup)} />
                </td>
              </tr>
            ))}

            {!loading && tab === 'views' && views.map((entry, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                  {formatWhen(entry.viewedAt)}
                  {entry.viewCount > 1 && (
                    <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                      ×{entry.viewCount}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 font-medium text-slate-900">
                  {entry.userName}
                </td>
                <td className="px-4 py-2.5 text-slate-700">
                  <span className="font-mono text-xs text-slate-600">
                    {entry.entityLabel ?? entry.entityId.slice(0, 8)}
                  </span>{' '}
                  <span className="text-xs text-slate-400">({entry.entityType})</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">
                  {entry.ip ?? '—'}
                </td>
              </tr>
            ))}

            {!loading && tab === 'logins' && logins.map((entry) => (
              <tr key={entry.userId} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                  {entry.lastLoginAt ? formatWhen(entry.lastLoginAt) : (
                    <span className="text-slate-400">Never signed in</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className="block font-medium text-slate-900">{entry.userName}</span>
                  {entry.email && (
                    <span className="block text-xs text-slate-400">{entry.email}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                  {entry.roleName}
                  {entry.status !== 'ACTIVE' && (
                    <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                      {entry.status.toLowerCase()}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                  {entry.lastLoginAt ? clientLabel(entry.userAgent) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row detail: every changed field, old value red, new value green. */}
      <SidePanel
        open={selected !== null}
        title={
          selected
            ? `${verbLabel(selected.verb)} ${selected.entityLabel ?? selected.entityType}`
            : ''
        }
        description={
          selected
            ? `${selected.actorName ?? 'System'} · ${formatWhen(selected.createdAt)}`
            : undefined
        }
        onClose={() => setSelected(null)}
      >
        {selected && (
          <div className="space-y-4 px-6 py-5">
            {parseDiff(selected.diff, lookup).length === 0 && (
              <p className="text-sm text-slate-500">No field details were recorded for this entry.</p>
            )}
            {parseDiff(selected.diff, lookup).map((entry) => (
              <div key={entry.field}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {fieldLabel(entry.field)}
                </p>
                {entry.value !== undefined ? (
                  <p className="mt-1 break-words rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-100">
                    {entry.value}
                  </p>
                ) : (
                  <div className="mt-1 space-y-1.5">
                    <p className="break-words rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
                      <span className="mr-1.5 text-[10px] font-semibold uppercase text-red-400">Before</span>
                      {entry.from}
                    </p>
                    <p className="break-words rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-100">
                      <span className="mr-1.5 text-[10px] font-semibold uppercase text-emerald-500">After</span>
                      {entry.to}
                    </p>
                  </div>
                )}
              </div>
            ))}
            <p className="border-t border-slate-100 pt-3 text-xs text-slate-400">
              {selected.entityType} · {selected.entityLabel ?? selected.entityId}
            </p>
          </div>
        )}
      </SidePanel>

      {/* Pagination - pr-16 keeps Previous/Next clear of the chat bubble
          that floats fixed at the bottom-right corner. */}
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-between pr-16 text-sm text-slate-500">
          <span>Page {page} of {pages} · {total} entries</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
